import { usePutEstimateReadings } from "@features/mx/validations/hooks";
import { toast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { Info } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";

interface EstimationRule {
  name: string;
  description: string;
  calculation?: string;
  example?: string;
}

interface EstimateMissingReadingsDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  missingReadingsCount?: number;
  estimationRulesData?: EstimationRule[];
  estimationRules?: Record<string, boolean>;
}

export function EstimateMissingReadingsDialog({
  open,
  setOpen,
  missingReadingsCount = 0,
  estimationRulesData = [],
  estimationRules = {},
}: EstimateMissingReadingsDialogProps) {
  const { id } = useParams<{ id: string }>();
  const { mutate: estimateReading } = usePutEstimateReadings(id);
  const [selectedEstimationRule, setSelectedEstimationRule] = useState<
    string | null
  >(null);

  const handleEstimate = () => {
    estimateReading(
      {
        estimateAllMissingReadings: true,
      },
      {
        onSuccess: () => {
          toast({
            title: "Readings are estimating in background.",
            description:
              "Readings are being estimated according to rules. Please check after some time.",
          });
          setOpen(false);
        },
      }
    );
  };

  const handleClose = () => {
    setSelectedEstimationRule(null);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Estimate Missing Readings</DialogTitle>
          <DialogDescription>
            We found <span className="font-medium">{missingReadingsCount}</span>{" "}
            missing reading(s) in this cycle.The current reading will be
            estimated using the estimation rules listed below. Consumption will
            be calculated after the estimation is applied.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className={`rounded-md border p-3 flex items-start justify-between gap-3 ${
              selectedEstimationRule === "Historical Average"
                ? "bg-muted/40"
                : ""
            }`}
          >
            <div className="space-y-1">
              <p className="font-medium text-sm">Historical Average</p>
              <p className="text-xs text-muted-foreground">
                Estimate based on average consumption from previous periods.
              </p>
            </div>
          </div>

          <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
            Note: Estimation rules estimate the current meter reading. The
            consumption value is derived after estimating the current reading.
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleEstimate}
            disabled={missingReadingsCount === 0}
          >
            Estimate Readings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
